import React, { useCallback, useState } from 'react';
import { Header, Icon, Message, Segment, Label, Button, Popup } from 'semantic-ui-react';
import { DesignSystem } from '../../styles/designSystem';
import type { TimClientData, TimClientLine } from '../../services/timData/timDataService';
import type { ClientPartnerResponse } from '../../types/dtos';
import {
  compareCpfTimWithQsa,
  cpfQsaMatchColor,
  cpfQsaMatchIcon,
  cpfQsaMatchLabel,
  type CpfQsaMatchStatus,
} from '../../utils/cpfQsaMatch';
import {
  mapWhatsAppResultToStatus,
  whatsAppValidationService,
  type WhatsAppValidationStatus,
} from '../../services/whatsapp/whatsappValidationService';
import { formatPhoneProgressive } from '../../services/utils/stringUtils';

/** `null`/vazio/placeholder "0" nunca devem ser exibidos — a linha inteira é omitida. */
const isBlankText = (value: string | null | undefined): boolean => {
  if (value == null) return true;
  const trimmed = value.trim();
  return trimmed === '' || trimmed === '0';
};

const isBlankNumber = (value: number | null | undefined): boolean => value == null || value === 0;

const lineParcelasSortValue = (linha: TimClientLine): number => {
  const zerado = linha.zerado === true || isBlankText(linha.planoDados);
  if (zerado) return 0;
  if (linha.parcelasRestantes == null) return Number.POSITIVE_INFINITY;
  return linha.parcelasRestantes;
};

/** Evita shift de fuso ao formatar datas "YYYY-MM-DD" vindas da API (sem hora). */
const formatDateBR = (dateStr?: string | null): string | null => {
  if (!dateStr) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (match) {
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  }
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('pt-BR');
};

const formatCurrencyBRL = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Prefere Razão Social, cai para Nome Representante quando a primeira vier vazia/placeholder. */
const resolveClientName = (data: TimClientData): string | null => {
  if (!isBlankText(data.razaoSocial)) return data.razaoSocial as string;
  if (!isBlankText(data.nomeRepresentante)) return data.nomeRepresentante as string;
  return null;
};

const cardStyle: React.CSSProperties = {
  background: DesignSystem.components.card.background,
  borderRadius: DesignSystem.components.card.borderRadius,
  boxShadow: DesignSystem.components.card.boxShadow,
  border: DesignSystem.components.card.border,
  marginBottom: '16px',
};

const rowStyle: React.CSSProperties = { margin: '0 0 6px 0', color: DesignSystem.colors.neutral.gray.dark };

const metricValueLabelStyle: React.CSSProperties = {
  background: DesignSystem.components.tabs.active.background,
  color: DesignSystem.components.tabs.active.color,
  border: 'none',
};

/** Grade fixa: `auto` nas colunas da direita desalinha quando a linha zerada não tem plano. */
const linhasGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.6fr) 140px 112px 72px',
  gap: '8px',
  alignItems: 'center',
};

type LineWhatsAppState = {
  status: WhatsAppValidationStatus;
  validatedAt?: string | null;
  fromCache?: boolean;
};

const WhatsAppBadge: React.FC<{
  status: WhatsAppValidationStatus;
  validatedAt?: string | null;
  onVerify: () => void;
  verifying: boolean;
}> = ({ status, validatedAt, onVerify, verifying }) => {
  const tooltipBase = 'Clique para verificar WhatsApp desta linha (uma por vez).';
  const validatedLabel = validatedAt ? formatDateBR(validatedAt) : null;

  if (status === 'loading' || verifying) {
    return (
      <Label size="small" style={{ minWidth: '90px', textAlign: 'center' }}>
        <Icon loading name="spinner" />
        Verificando…
      </Label>
    );
  }

  if (status === 'has') {
    return (
      <Popup
        content={validatedLabel ? `Validado em ${validatedLabel}` : tooltipBase}
        trigger={
          <Label size="small" color="green" style={{ minWidth: '90px', textAlign: 'center', cursor: 'pointer' }} onClick={onVerify}>
            <Icon name="check" />
            WhatsApp
          </Label>
        }
      />
    );
  }

  if (status === 'no') {
    return (
      <Popup
        content={validatedLabel ? `Validado em ${validatedLabel}` : tooltipBase}
        trigger={
          <Label size="small" color="grey" style={{ minWidth: '90px', textAlign: 'center', cursor: 'pointer' }} onClick={onVerify}>
            <Icon name="times" />
            Sem WA
          </Label>
        }
      />
    );
  }

  if (status === 'error') {
    return (
      <Popup
        content="Não foi possível validar — tente novamente."
        trigger={
          <Label size="small" color="orange" style={{ minWidth: '90px', textAlign: 'center', cursor: 'pointer' }} onClick={onVerify}>
            <Icon name="question" />
            ?
          </Label>
        }
      />
    );
  }

  return (
    <Popup
      content={tooltipBase}
      trigger={
        <Button size="mini" basic onClick={onVerify} style={{ minWidth: '90px' }}>
          <Icon name="whatsapp" />
          Verificar
        </Button>
      }
    />
  );
};

export interface TimClientDataCardsProps {
  data: TimClientData;
  /** Sócios (QSA) do cliente carregado via CNPJ WS — para comparação CPF TIM × QSA. */
  partners?: ClientPartnerResponse[];
  /** Mostra aviso + desabilita ação quando não há nenhuma linha TIM (usado pelo botão flutuante). */
  emptyLinesWarning?: boolean;
}

/**
 * Cards reutilizáveis (Resumo / Comparação QSA / Financeiro / Linhas) + alerta de desatualização,
 * usados tanto na tab "Dados TIM" do modal de contrato quanto no modal do botão flutuante.
 */
export const TimClientDataCards: React.FC<TimClientDataCardsProps> = ({
  data,
  partners = [],
  emptyLinesWarning = false,
}) => {
  const [whatsappByLine, setWhatsappByLine] = useState<Record<string, LineWhatsAppState>>({});
  const [verifyingLine, setVerifyingLine] = useState<string | null>(null);

  const clientName = resolveClientName(data);
  const hasTelefone = !isBlankText(data.telefoneContato);
  const hasEmail = !isBlankText(data.email);
  const hasCpf = !isBlankText(data.cpf);
  const hasRg = !isBlankText(data.rg);
  const hasValorFatura = !isBlankNumber(data.valorFatura);
  const hasFidelidade = data.fidelidadeMesesRestantes != null && data.fidelidadeMesesRestantes > 0;
  const isInadimplente = data.inadimplente === true;
  const hasLinhasAptas = data.linhasAptas != null;
  const hasAcessosAtivos = data.acessosAtivos != null;
  const hasZerados = data.zerados != null;
  const hasMetricas = hasLinhasAptas || hasAcessosAtivos || hasZerados;
  const linhas = (data.linhas ?? [])
    .filter((linha) => !isBlankText(linha.numero))
    .slice()
    .sort((a, b) => lineParcelasSortValue(a) - lineParcelasSortValue(b));
  const hasLinhas = linhas.length > 0;
  const loteData = formatDateBR(data.loteImportacaoData);

  const hasResumoContent = !!clientName || hasCpf || hasRg || hasTelefone || hasEmail || isInadimplente;
  const hasFinanceiroContent = hasValorFatura || hasFidelidade || hasMetricas;
  const activePartners = partners.filter((p) => !p.isDeleted);
  const showQsaComparison = hasCpf && activePartners.length > 0;

  const handleVerifyWhatsApp = useCallback(async (numero: string) => {
    if (verifyingLine) return;
    setVerifyingLine(numero);
    setWhatsappByLine((prev) => ({
      ...prev,
      [numero]: { ...prev[numero], status: 'loading' },
    }));

    try {
      const result = await whatsAppValidationService.validatePhone(numero);
      const status = mapWhatsAppResultToStatus(result);
      setWhatsappByLine((prev) => ({
        ...prev,
        [numero]: {
          status,
          validatedAt: result.validatedAt,
          fromCache: result.fromCache,
        },
      }));
    } catch {
      setWhatsappByLine((prev) => ({
        ...prev,
        [numero]: { status: 'error' },
      }));
    } finally {
      setVerifyingLine(null);
    }
  }, [verifyingLine]);

  const renderParcelasCell = (parcelas: number | null | undefined) => {
    if (parcelas == null) {
      return (
        <span style={{ color: DesignSystem.colors.neutral.gray.dark, fontSize: '12px' }}>
          Aguardando atualização
        </span>
      );
    }
    return (
      <Label size="tiny" style={{ margin: 0 }}>
        {parcelas} {parcelas === 1 ? 'mês' : 'meses'}
      </Label>
    );
  };

  return (
    <div>
      <Message
        info
        style={{ marginBottom: '20px', borderLeft: `4px solid ${DesignSystem.colors.status.info}` }}
      >
        <Icon name="clock" />
        Dados podem estar desatualizados{loteData ? ` — última importação do lote: ${loteData}` : ''}.
      </Message>

      {hasResumoContent && (
        <Segment style={cardStyle}>
          <Header as="h4" style={{ color: DesignSystem.colors.neutral.black }}>
            <Icon name="id card outline" />
            Resumo do cliente
          </Header>
          {clientName && (
            <p style={rowStyle}>
              <strong>Nome:</strong> {clientName}
            </p>
          )}
          {isInadimplente && (
            <p style={rowStyle}>
              <Label size="small" color="red">
                <Icon name="warning sign" />
                Inadimplente
              </Label>
            </p>
          )}
          {hasCpf && (
            <p style={rowStyle}>
              <strong>CPF (base TIM):</strong> {data.cpf}{' '}
              <Label size="tiny" color="orange" style={{ marginLeft: '4px' }}>
                <Icon name="warning sign" />
                não validado, pode estar incompleto
              </Label>
            </p>
          )}
          {hasRg && (
            <p style={rowStyle}>
              <strong>RG (base TIM):</strong> {data.rg}
            </p>
          )}
          {hasTelefone && (
            <p style={rowStyle}>
              <strong>Telefone:</strong> {formatPhoneProgressive(data.telefoneContato as string)}
            </p>
          )}
          {hasEmail && (
            <p style={rowStyle}>
              <strong>E-mail:</strong> {data.email}
            </p>
          )}
        </Segment>
      )}

      {showQsaComparison && (
        <Segment style={cardStyle}>
          <Header as="h4" style={{ color: DesignSystem.colors.neutral.black }}>
            <Icon name="balance scale" />
            Comparação CPF TIM × QSA
          </Header>
          <Message size="tiny" info style={{ marginBottom: '12px' }}>
            <Icon name="info circle" />
            CPF do sócio via Receita pode estar mascarado; comparação não substitui validação manual.
          </Message>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
              <p style={{ ...rowStyle, fontWeight: 600, marginBottom: '8px' }}>CPF TIM</p>
              <p style={rowStyle}>{data.cpf}</p>
              <Label size="tiny" color="orange">
                <Icon name="warning sign" />
                baixa confiança
              </Label>
            </div>
            <div style={{ flex: '2 1 280px' }}>
              <p style={{ ...rowStyle, fontWeight: 600, marginBottom: '8px' }}>Sócios (QSA)</p>
              {activePartners.map((partner) => {
                const matchStatus: CpfQsaMatchStatus = compareCpfTimWithQsa(data.cpf, partner.cpfCnpj);
                return (
                  <div
                    key={partner.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 0',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <span style={{ color: DesignSystem.colors.neutral.black }}>{partner.nome}</span>
                      {partner.cpfCnpj && (
                        <span style={{ color: DesignSystem.colors.neutral.gray.dark, marginLeft: '8px', fontSize: '12px' }}>
                          {partner.cpfCnpj}
                        </span>
                      )}
                    </div>
                    <Popup
                      content={cpfQsaMatchLabel[matchStatus]}
                      trigger={
                        <Label size="tiny" color={cpfQsaMatchColor[matchStatus]}>
                          <Icon name={cpfQsaMatchIcon[matchStatus]} />
                          {cpfQsaMatchLabel[matchStatus]}
                        </Label>
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </Segment>
      )}

      {hasFinanceiroContent && (
        <Segment style={cardStyle}>
          <Header as="h4" style={{ color: DesignSystem.colors.neutral.black }}>
            <Icon name="dollar" />
            Financeiro / Fidelidade
          </Header>
          {hasValorFatura && (
            <p style={rowStyle}>
              <strong>Valor da fatura:</strong> {formatCurrencyBRL(data.valorFatura as number)}
            </p>
          )}
          {hasFidelidade && (
            <p style={rowStyle}>
              <strong>Fidelidade restante:</strong>{' '}
              <Label style={metricValueLabelStyle}>
                {data.fidelidadeMesesRestantes}{' '}
                {data.fidelidadeMesesRestantes === 1 ? 'mês restante' : 'meses restantes'}
              </Label>
            </p>
          )}
          {hasLinhasAptas && (
            <p style={rowStyle}>
              <strong>Linhas aptas:</strong>{' '}
              <Label style={metricValueLabelStyle}>{data.linhasAptas}</Label>
            </p>
          )}
          {hasAcessosAtivos && (
            <p style={rowStyle}>
              <strong>Acessos ativos:</strong>{' '}
              <Label style={metricValueLabelStyle}>{data.acessosAtivos}</Label>
            </p>
          )}
          {hasZerados && (
            <p style={rowStyle}>
              <strong>Zerados:</strong>{' '}
              <Label style={metricValueLabelStyle}>{data.zerados}</Label>
            </p>
          )}
        </Segment>
      )}

      <Segment style={cardStyle}>
        <Header as="h4" style={{ color: DesignSystem.colors.neutral.black }}>
          <Icon name="mobile alternate" />
          Linhas TIM já ativas {hasLinhas ? `(${linhas.length})` : ''}
        </Header>
        {hasLinhas ? (
          <div>
            <div
              style={{
                ...linhasGridStyle,
                padding: '4px 0 8px',
                fontSize: '11px',
                fontWeight: 600,
                color: DesignSystem.colors.neutral.gray.dark,
                textTransform: 'uppercase',
              }}
            >
              <span>Número</span>
              <span style={{ textAlign: 'center' }}>Parcelas restantes</span>
              <span style={{ textAlign: 'center' }}>WhatsApp</span>
              <span style={{ textAlign: 'right' }}>Plano</span>
            </div>
            {linhas.map((linha, index) => {
              const numero = linha.numero as string;
              const waState = whatsappByLine[numero] ?? { status: 'unknown' as WhatsAppValidationStatus };
              const isZerado = linha.zerado === true || isBlankText(linha.planoDados);
              return (
                <div
                  key={`${numero}-${index}`}
                  style={{
                    ...linhasGridStyle,
                    padding: '8px 0',
                    borderBottom: index < linhas.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 0 }}>
                    <Icon name="mobile alternate" color="blue" />
                    <span style={{ color: DesignSystem.colors.neutral.black }}>{formatPhoneProgressive(numero)}</span>
                    {isZerado && (
                      <Label size="tiny" color="orange" style={{ margin: 0 }}>
                        Zerado
                      </Label>
                    )}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    {renderParcelasCell(isZerado ? 0 : linha.parcelasRestantes)}
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <WhatsAppBadge
                      status={waState.status}
                      validatedAt={waState.validatedAt}
                      onVerify={() => handleVerifyWhatsApp(numero)}
                      verifying={verifyingLine === numero}
                    />
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {!isBlankText(linha.planoDados) && (
                      <Label size="tiny" color="blue" style={{ margin: 0 }}>
                        {linha.planoDados}
                      </Label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          emptyLinesWarning && (
            <Message warning style={{ margin: 0 }}>
              <Icon name="warning sign" />
              Nenhuma linha TIM encontrada para este CNPJ.
            </Message>
          )
        )}
      </Segment>
    </div>
  );
};

export default TimClientDataCards;
